import type { Auftrag, GesamtUmfang } from '@awf/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DIENST_NICHT_ERREICHBAR_TEXT } from '../api/errors.js';
import { istSichtbar } from './einsicht.js';
import type { AuftragsQuelle } from './runs-source.js';

export type AuftragsZustand =
  | { art: 'laedt' }
  | { art: 'daten'; auftraege: Auftrag[] }
  | { art: 'fehler'; text: string; wiederholen: () => void };

export type EinzelZustand =
  | { art: 'laedt' }
  | { art: 'daten'; auftrag: Auftrag | null }
  | { art: 'fehler'; text: string; wiederholen: () => void };

/**
 * Wie lange auf die erste Aufnahme gewartet wird, bevor die Ansicht einen Fehler meldet.
 *
 * Noetig, weil Firestore Netzwerkverlust als `UNAVAILABLE` fuehrt und das in
 * `isPermanentError` NICHT als permanent gilt: die Bibliothek wiederholt still und ruft
 * den Fehler-Callback nie (belegt am SDK-Quelltext, `remote/rpc_error.ts`). Nur
 * `PERMISSION_DENIED` und `FAILED_PRECONDITION` melden sich. Ohne diese Grenze bliebe die
 * Liste bei fehlender Verbindung fuer immer auf "wird geladen" -- genau das endlose Warten,
 * das Spec Abschnitt 6.1 ausschliesst.
 *
 * 15 Sekunden: lang genug fuer einen langsamen Erstverbindungsaufbau, kurz genug, dass
 * niemand davor sitzt und raetselt.
 */
export const LADE_ZEITGRENZE_MS = 15_000;

const ZEITGRENZE_TEXT =
  'Ihre Aufträge konnten nicht geladen werden. Bitte prüfen Sie Ihre Verbindung.';

/**
 * Gemeinsamer Kern beider Hooks: Beobachtung starten, auf die erste Aufnahme warten, bei
 * Ausbleiben oder Abbruch in den Fehlerzustand gehen -- und einen Weg zurueck anbieten.
 *
 * Ein Abbruch wird NIE zum leeren Ergebnis: Abnahmekriterium 8 verlangt eine
 * Fehlermeldung, sonst sieht ein nicht erreichbarer Dienst aus wie "keine Auftraege".
 * Deshalb ersetzt der Fehlerzustand auch bereits gezeigte Daten.
 */
function useBeobachtung<T>(
  starte: (
    aufDaten: (wert: T, ausCache: boolean) => void,
    aufFehler: () => void,
  ) => (() => void) | undefined,
  abhaengigkeiten: readonly unknown[],
):
  | { art: 'laedt' }
  | { art: 'daten'; wert: T }
  | { art: 'fehler'; text: string; wiederholen: () => void } {
  const [versuch, setVersuch] = useState(0);
  const [zustand, setZustand] = useState<
    { art: 'laedt' } | { art: 'daten'; wert: T } | { art: 'fehler'; text: string }
  >({ art: 'laedt' });

  const wiederholen = useCallback(() => {
    setZustand({ art: 'laedt' });
    setVersuch((n) => n + 1);
  }, []);

  useEffect(() => {
    setZustand({ art: 'laedt' });

    // Vor dem ersten Serverstand ist eine Aufnahme aus dem Zwischenspeicher wertlos: sie
    // ist leer, weil nichts geladen wurde, nicht weil nichts da ist. Sie wird verworfen,
    // die Uhr laeuft weiter -- und meldet dann ehrlich einen Fehler. Danach ist der
    // Zwischenspeicher die letzte bekannte Wahrheit und darf angezeigt werden, sonst
    // verliert der Nutzer bei jedem Wackler alles.
    let vomServerGesehen = false;
    let fehlerGesehen = false;
    // Erst nach `starte` gestellt: Liefert die Attrappe oder der Snapshot synchron, ist die
    // Uhr sonst schon wieder ueberfluessig, und ohne Abo (kein Nutzer, oder der Listen-Listener
    // ist noch nicht angefordert) gibt es nichts, worauf zu warten waere. Eine Zeitgrenze ohne
    // Abo meldete einen Fehler, den es nicht gibt (Spec C, Abschnitt 6).
    let uhr: ReturnType<typeof setTimeout> | undefined;

    const abbestellen = starte(
      (wert, ausCache) => {
        if (ausCache && !vomServerGesehen) return;
        if (!ausCache) vomServerGesehen = true;
        clearTimeout(uhr);
        setZustand({ art: 'daten', wert });
      },
      () => {
        fehlerGesehen = true;
        clearTimeout(uhr);
        setZustand({ art: 'fehler', text: DIENST_NICHT_ERREICHBAR_TEXT });
      },
    );

    if (abbestellen && !vomServerGesehen && !fehlerGesehen) {
      uhr = setTimeout(
        () => setZustand({ art: 'fehler', text: ZEITGRENZE_TEXT }),
        LADE_ZEITGRENZE_MS,
      );
    }

    return () => {
      clearTimeout(uhr);
      abbestellen?.();
    };
    // `starte` steht bewusst NICHT in der Liste: Es kommt als Inline-Funktion herein und
    // waere bei jedem Rendern neu -- die Beobachtung wuerde endlos ab- und wieder
    // aufgebaut. Gesteuert wird sie ueber `abhaengigkeiten`, die der Aufrufer angibt.
  }, [...abhaengigkeiten, versuch]);

  // Stabile Referenz: Wer diesen Wert als useEffect-Abhaengigkeit benutzt, bekaeme sonst
  // bei jedem Rendern ein neues Objekt und damit eine Endlosschleife.
  return useMemo(
    () => (zustand.art === 'fehler' ? { ...zustand, wiederholen } : zustand),
    [zustand, wiederholen],
  );
}

export function useAuftraege(
  quelle: AuftragsQuelle,
  grenze: number,
  uid: string | null,
): AuftragsZustand {
  const zustand = useBeobachtung<Auftrag[]>(
    (aufDaten, aufFehler) => (uid ? quelle.beobachte(uid, grenze, aufDaten, aufFehler) : undefined),
    [quelle, grenze, uid],
  );

  return useMemo(
    () => (zustand.art === 'daten' ? { art: 'daten', auftraege: zustand.wert } : zustand),
    [zustand],
  );
}

/**
 * Die wartenden Laeufe aus der eigenen, ungedeckelten Abfrage.
 *
 * Bewusst ein eigener Zustand und nicht in `useAuftraege` verwoben: Scheitert diese Abfrage
 * -- etwa weil der Composite-Index fehlt --, soll die Hauptliste stehen bleiben und der
 * Ausfall trotzdem sichtbar werden. Ein stiller Ausfall waere schlimmer als gar keine zweite
 * Abfrage, weil dann genau der Lauf spurlos verschwindet, auf den ein Mensch wartet.
 */
export function useWartende(
  quelle: AuftragsQuelle,
  grenze: number,
  uid: string | null,
): AuftragsZustand {
  const zustand = useBeobachtung<Auftrag[]>(
    (aufDaten, aufFehler) =>
      uid ? quelle.beobachteWartende(uid, grenze, aufDaten, aufFehler) : undefined,
    [quelle, grenze, uid],
  );

  return useMemo(
    () => (zustand.art === 'daten' ? { art: 'daten', auftraege: zustand.wert } : zustand),
    [zustand],
  );
}

/**
 * Die Gesamtansicht (`beobachteGesamt`). `uid` steuert nur, OB beobachtet wird und wann neu:
 * Nach einem Kontowechsel darf die Liste des vorigen Kontos nicht stehen bleiben.
 */
export function useGesamtAuftraege(
  quelle: AuftragsQuelle,
  grenze: number,
  umfang: GesamtUmfang | null,
  uid: string | null,
): AuftragsZustand {
  const zustand = useBeobachtung<Auftrag[]>(
    (aufDaten, aufFehler) =>
      uid && umfang ? quelle.beobachteGesamt(umfang, grenze, aufDaten, aufFehler) : undefined,
    [quelle, grenze, umfang, uid],
  );

  return useMemo(
    () => (zustand.art === 'daten' ? { art: 'daten', auftraege: zustand.wert } : zustand),
    [zustand],
  );
}

export function useAuftrag(quelle: AuftragsQuelle, runId: string): EinzelZustand {
  const zustand = useBeobachtung<Auftrag | null>(
    (aufDaten, aufFehler) => quelle.beobachteEinen(runId, aufDaten, aufFehler),
    [quelle, runId],
  );

  // Ein ausgeblendeter ("geloeschter") Auftrag gilt hier als nicht vorhanden, wie in den
  // Leserouten des Backends: Detail-, Freigabe- und Ergebnisseite zeigen ihn nicht mehr.
  return useMemo(
    () =>
      zustand.art === 'daten'
        ? { art: 'daten', auftrag: zustand.wert && istSichtbar(zustand.wert) ? zustand.wert : null }
        : zustand,
    [zustand],
  );
}
